import type { PortalMakePaymentContextDto } from '@supply-mover-2-0/shared';
import { Tooltip, useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX, ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { formatCurrency } from '../lib/formatCurrency';
import { formatStatementDate } from '../lib/formatStatementDate';
import { CurrencyInput } from './CurrencyInput';
import { HistoryFilters, resolveHistoryRange } from './HistoryFilterBar';
import { InvoiceDueChip } from './InvoiceStatusChips';

/** One invoice's line: whether it is being paid, and how much (as typed). */
export interface InvoicePaymentLineModel {
	checked: boolean;
	amount: string;
}

export type InvoicePaymentLinesModel = Record<number, InvoicePaymentLineModel>;

export type OpenInvoicePickerRowModel = Pick<
	PortalMakePaymentContextDto['openInvoices'][number],
	'id' | 'invoiceNumber' | 'date' | 'dueDate' | 'outstandingBalance'
>;

interface OpenInvoicePickerProps {
	invoices: Array<OpenInvoicePickerRowModel>;
	lines: InvoicePaymentLinesModel;
	/** Receives an updater, so the caller can wrap its own state setter. */
	onLinesChange: (
		update: (prev: InvoicePaymentLinesModel) => InvoicePaymentLinesModel,
	) => void;
	lineErrors: Record<number, string>;
	/** Extra control at the end of a row (the payment link's invoice PDF). */
	renderRowAction?: (invoice: OpenInvoicePickerRowModel) => ReactNode;
}

/**
 * The open-invoice picker from the signed-in Make a Payment page (PD-14172,
 * PD-14701), shared with the public statement payment link (PD-16674) so both
 * pay invoices the same way: tick the invoices to pay, change what each one
 * gets, with the same select-all and date and overdue filters.
 *
 * It owns only its filters. The caller owns the lines, because it totals,
 * validates and submits them.
 */
export function OpenInvoicePicker({
	invoices,
	lines,
	onLinesChange,
	lineErrors,
	renderRowAction,
}: OpenInvoicePickerProps): JSX.Element {
	const { t } = useTranslation();

	// Filters live in the same Apply/Cancel tray as the Invoices list
	// (PD-14701): a relative date range (defaulting to ALL TIME — hiding an
	// old unpaid invoice by default would hide exactly what most needs
	// paying) plus an Overdue-only toggle.
	const [range, setRange] = useState('');
	// Explicit bounds for the 'custom' range (PD-14899); only meaningful —
	// and only retained — while `range` is 'custom'.
	const [rangeFrom, setRangeFrom] = useState('');
	const [rangeTo, setRangeTo] = useState('');
	const [overdueOnly, setOverdueOnly] = useState(false);
	const selectAllRef = useRef<HTMLInputElement>(null);

	// Date columns come back as `YYYY-MM-DD` (or an ISO timestamp); compare on
	// the date portion only, lexicographically — valid for ISO date strings.
	const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);

	const filteredInvoices = useMemo(() => {
		// '' = the page default (all time); resolveHistoryRange('all') → no
		// bounds. Any explicit pick resolves to inclusive ISO date bounds.
		const bounds = resolveHistoryRange(
			range === '' ? 'all' : range,
			rangeFrom,
			rangeTo,
		);
		const dateFrom = bounds.dateFrom ?? '';
		const dateTo = bounds.dateTo ?? '';
		return invoices.filter((invoice) => {
			const invDate = invoice.date?.slice(0, 10) ?? null;
			if (dateFrom !== '' && (invDate == null || invDate < dateFrom)) {
				return false;
			}
			if (dateTo !== '' && (invDate == null || invDate > dateTo)) {
				return false;
			}
			if (overdueOnly) {
				const due = invoice.dueDate?.slice(0, 10) ?? null;
				if (due == null || due >= todayIso) {
					return false;
				}
			}
			return true;
		});
	}, [invoices, range, rangeFrom, rangeTo, overdueOnly, todayIso]);

	const filtersActive = (range !== '' && range !== 'all') || overdueOnly;

	const allFilteredChecked =
		filteredInvoices.length > 0 &&
		filteredInvoices.every((inv) => lines[inv.id]?.checked === true);
	const someFilteredChecked = filteredInvoices.some(
		(inv) => lines[inv.id]?.checked === true,
	);

	// Native checkboxes have no React-controlled "indeterminate" — set it
	// imperatively for the partial-selection state.
	useEffect(() => {
		if (selectAllRef.current != null) {
			selectAllRef.current.indeterminate =
				someFilteredChecked && !allFilteredChecked;
		}
	}, [someFilteredChecked, allFilteredChecked]);

	// A newly ticked invoice starts at its full balance.
	const toggleLine = (invoiceId: number, outstanding: number): void => {
		onLinesChange((prev) => {
			const current = prev[invoiceId] ?? { checked: false, amount: '' };
			const checked = !current.checked;
			return {
				...prev,
				[invoiceId]: {
					checked,
					amount: checked ? outstanding.toFixed(2) : '',
				},
			};
		});
	};

	// Select-all toggles only the currently-visible (filtered) rows, defaulting
	// each to its full outstanding balance — matching a single-row check.
	const toggleSelectAll = (): void => {
		const shouldCheck = !allFilteredChecked;
		onLinesChange((prev) => {
			const next = { ...prev };
			for (const invoice of filteredInvoices) {
				next[invoice.id] = shouldCheck
					? { checked: true, amount: invoice.outstandingBalance.toFixed(2) }
					: { checked: false, amount: '' };
			}
			return next;
		});
	};

	const setLineAmount = (invoiceId: number, amount: string): void => {
		onLinesChange((prev) => ({
			...prev,
			[invoiceId]: { checked: true, amount },
		}));
	};

	return (
		<>
			{/*
			  Filters (PD-14172, tray rework per PD-14701) — one row: the
			  unlabeled select-all (bulk selector) and the same Apply/Cancel
			  filter tray as the Invoices list, holding the relative date range
			  (default: all time) and the Overdue-only toggle.
			*/}
			<div className="flex items-center gap-3 mb-3">
				{filteredInvoices.length > 0 && (
					<Tooltip content={t('Select all')} position="top">
						<label className="flex items-center min-h-touch cursor-pointer">
							<input
								ref={selectAllRef}
								type="checkbox"
								aria-label={t('Select all')}
								checked={allFilteredChecked}
								onChange={toggleSelectAll}
								className="w-4 h-4 text-portal-accent"
							/>
						</label>
					</Tooltip>
				)}
				<div className="flex-1 min-w-0">
					<HistoryFilters
						fields={[
							{
								kind: 'range',
								key: 'range',
								label: 'Date Range',
								defaultRange: 'all',
							},
							{ kind: 'toggle', key: 'overdue', label: 'Overdue only' },
						]}
						values={{
							range,
							from: rangeFrom,
							to: rangeTo,
							overdue: overdueOnly ? '1' : '',
						}}
						onApply={(next) => {
							// Both keys are always present: the tray drafts from
							// `values` above and Clear-all writes '' per field.
							// A custom pick with both bounds blank is not a
							// filter — collapse it to the page default (PD-14899).
							const hasCustomBounds =
								next.range === 'custom' &&
								(next.from !== '' || next.to !== '');
							setRange(
								next.range === 'all' ||
									(next.range === 'custom' && !hasCustomBounds)
									? ''
									: next.range,
							);
							setRangeFrom(hasCustomBounds ? next.from : '');
							setRangeTo(hasCustomBounds ? next.to : '');
							setOverdueOnly(next.overdue === '1');
						}}
					/>
				</div>
			</div>

			{filteredInvoices.length === 0 ? (
				<div className="text-sm text-brand-muted">
					<p>{t('No invoices match your filters.')}</p>
					{filtersActive && (
						<button
							type="button"
							onClick={() => {
								setRange('');
								setRangeFrom('');
								setRangeTo('');
								setOverdueOnly(false);
							}}
							className="mt-1 text-xs font-medium text-portal-accent hover:underline"
						>
							{t('Clear filters')}
						</button>
					)}
				</div>
			) : (
				<div className="max-h-96 overflow-y-auto overflow-x-hidden space-y-2">
					{filteredInvoices.map((invoice) => {
						const line = lines[invoice.id] ?? {
							checked: false,
							amount: '',
						};
						return (
							<div
								key={invoice.id}
								className="flex flex-wrap items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-brand-input"
							>
								<input
									type="checkbox"
									aria-label={`${t('Pay invoice')} ${t('#')}${t(invoice.invoiceNumber)}`}
									checked={line.checked}
									onChange={() => {
										toggleLine(invoice.id, invoice.outstandingBalance);
									}}
									className="w-4 h-4 text-portal-accent"
								/>
								<div className="flex-1 min-w-[120px]">
									<div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
										<p className="text-sm font-medium text-default">
											{t('Invoice')} #{t(invoice.invoiceNumber)}
										</p>
										{/* Same urgency chip as the Invoices list (PD-14697).
										    Open invoices have no `total` here; passing null
										    reads as partial, which the due chip treats the
										    same as unpaid. */}
										<InvoiceDueChip
											invoice={{
												outstandingBalance: String(invoice.outstandingBalance),
												total: null,
												dueDate: invoice.dueDate ?? null,
											}}
										/>
									</div>
									<p className="text-xs text-brand-muted">
										{t('Outstanding')}:{' '}
										{t(formatCurrency(invoice.outstandingBalance) ?? '')}
										{invoice.date != null && (
											<>
												{' · '}
												{t('Dated')} {t(formatStatementDate(invoice.date))}
											</>
										)}
									</p>
								</div>
								<div className="w-32">
									{/* PD-15974 — the CRM's cents-shift currency mask; see CurrencyInput. */}
									<CurrencyInput
										value={line.amount}
										disabled={!line.checked}
										onChange={(next) => {
											setLineAmount(invoice.id, next);
										}}
										className="text-right disabled:opacity-70"
									/>
								</div>
								{renderRowAction?.(invoice)}
								{(lineErrors[invoice.id] as string | undefined) != null && (
									<p className="w-full text-xs text-danger text-right">
										{t(lineErrors[invoice.id])}
									</p>
								)}
							</div>
						);
					})}
				</div>
			)}
		</>
	);
}
